// src/lib/sam-dev-api.ts
// Local development API for SAM.gov batch import when edge functions aren't deployed

const SAM_PROXY_BASE = '/api/sam';

interface ImportConfig {
  batchSize: number;
  naicsCodes?: string[];
  agencies?: string[];
  responseDateStart?: string;
  responseDateEnd?: string;
  postedDateStart?: string;
  postedDateEnd?: string;
  keywords?: string;
  setAsideTypes?: string[];
  noticeTypes?: string[];
  onlyActive?: boolean;
  maxRecords?: number;
  startOffset?: number;
}

interface ImportResult {
  success: boolean;
  totalRecords: number;
  importedRecords: number;
  failedRecords: number;
  skippedRecords: number;
  errors: string[];
  duration: number;
  batchesProcessed: number;
  jobId: string;
  lastOffset?: number;
  hasMore?: boolean;
  canResume?: boolean;
  nextResumeOffset?: number;
}

// Helper to format dates for SAM.gov API (MM/dd/yyyy)
function formatDateForSAM(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

// Check if we have a SAM API key available (via Vite environment)
export function hasSamApiKey(): boolean {
  // In development, check if the key is set
  return !!import.meta.env.VITE_SAM_API_KEY;
}

// Get API key for direct requests
function getApiKey(): string | null {
  return import.meta.env.VITE_SAM_API_KEY || null;
}

/**
 * Local development implementation of SAM.gov batch import
 * This bypasses Supabase edge functions and calls SAM.gov directly via Vite proxy
 */
export async function localBatchImport(config: ImportConfig): Promise<ImportResult> {
  const startTime = Date.now();
  const jobId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const result: ImportResult = {
    success: false,
    totalRecords: 0,
    importedRecords: 0,
    failedRecords: 0,
    skippedRecords: 0,
    errors: [],
    duration: 0,
    batchesProcessed: 0,
    jobId,
    lastOffset: 0,
    hasMore: false,
    canResume: false,
    nextResumeOffset: 0
  };

  const apiKey = getApiKey();
  if (!apiKey) {
    result.errors.push('SAM API key not configured. Set VITE_SAM_API_KEY in your .env file.');
    result.duration = Date.now() - startTime;
    return result;
  }

  try {
    const batchSize = Math.min(config.batchSize || 100, 1000);
    let offset = config.startOffset || 0;
    const maxRecords = config.maxRecords || 100;
    const maxBatches = 10; // Limit batches in dev mode
    let batchCount = 0;

    while (batchCount < maxBatches && result.importedRecords < maxRecords) {
      // Build search parameters
      const params = new URLSearchParams();
      params.set('offset', offset.toString());
      params.set('limit', batchSize.toString());

      // Notice type (ptype): explicit selection wins (e.g. RFIs = 'r,s'); otherwise
      // default to the active opportunity set incl. Special Notice.
      const validPtypes = new Set(['o', 'k', 'p', 'r', 's', 'a', 'i', 'u', 'g']);
      const selectedNoticeTypes = (config.noticeTypes || []).filter(t => validPtypes.has(t));
      if (selectedNoticeTypes.length > 0) {
        params.set('ptype', selectedNoticeTypes.join(','));
      } else if (config.onlyActive !== false) {
        params.set('ptype', 'p,o,k,r,s');
      }

      // Date filters
      if (config.postedDateStart) {
        params.set('postedFrom', formatDateForSAM(config.postedDateStart));
      } else {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        params.set('postedFrom', formatDateForSAM(thirtyDaysAgo));
      }

      if (config.postedDateEnd) {
        params.set('postedTo', formatDateForSAM(config.postedDateEnd));
      } else {
        params.set('postedTo', formatDateForSAM(new Date()));
      }

      if (config.responseDateStart) {
        params.set('rdlfrom', formatDateForSAM(config.responseDateStart));
      }
      if (config.responseDateEnd) {
        params.set('rdlto', formatDateForSAM(config.responseDateEnd));
      }

      // NAICS filter
      if (config.naicsCodes && config.naicsCodes.length > 0) {
        params.set('naicsCode', config.naicsCodes.join('~'));
      }

      // Keywords
      if (config.keywords) {
        params.set('q', config.keywords);
      }

      // Set-aside filter
      if (config.setAsideTypes && config.setAsideTypes.length > 0) {
        params.set('typeOfSetAside', config.setAsideTypes.join(','));
      }

      // Agency filter
      if (config.agencies && config.agencies.length > 0) {
        params.set('deptname', config.agencies.join(','));
      }

      console.log(`[Dev Import] Fetching batch ${batchCount + 1}: offset=${offset}, limit=${batchSize}`);

      // Call SAM.gov via Vite proxy
      const response = await fetch(`${SAM_PROXY_BASE}/opportunities/v2/search?${params.toString()}`, {
        method: 'GET',
        headers: {
          'X-Api-Key': apiKey,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Dev Import] API error:', response.status, errorText);
        result.errors.push(`API Error: ${response.status} - ${errorText.substring(0, 200)}`);
        break;
      }

      const data = await response.json();
      const opportunities = data.opportunitiesData || [];
      result.totalRecords = data.totalRecords || 0;

      console.log(`[Dev Import] Received ${opportunities.length} opportunities (total: ${result.totalRecords})`);

      if (opportunities.length === 0) {
        break;
      }

      // Client-side NAICS filtering (SAM.gov API often ignores NAICS filter)
      let filtered = opportunities;
      if (config.naicsCodes && config.naicsCodes.length > 0) {
        const requestedCodes = new Set(config.naicsCodes);
        filtered = opportunities.filter((opp: any) => {
          const code = opp.naicsCode?.toString().trim();
          return code && requestedCodes.has(code);
        });
        console.log(`[Dev Import] NAICS filter: ${filtered.length}/${opportunities.length} match`);
      }

      result.skippedRecords += opportunities.length - filtered.length;

      // Note: In dev mode, we just count records but don't save to database
      // The actual database insert would need the edge function or backend
      result.importedRecords += filtered.length;
      result.batchesProcessed++;
      batchCount++;
      offset += batchSize;

      // Check if more records available
      if (opportunities.length < batchSize) {
        break;
      }

      // Small delay between batches
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    result.lastOffset = offset;
    result.hasMore = offset < result.totalRecords;
    result.canResume = result.hasMore;
    result.nextResumeOffset = offset;
    result.success = true;
    result.duration = Date.now() - startTime;

    console.log(`[Dev Import] Complete: ${result.importedRecords} records processed in ${result.duration}ms`);

  } catch (error) {
    console.error('[Dev Import] Error:', error);
    result.errors.push(error instanceof Error ? error.message : 'Unknown error');
    result.duration = Date.now() - startTime;
  }

  return result;
}

/**
 * Check if the Vite SAM proxy is working
 */
export async function testLocalSamConnection(): Promise<{ success: boolean; message: string }> {
  const apiKey = getApiKey();

  if (!apiKey) {
    return {
      success: false,
      message: 'No SAM API key configured. Add VITE_SAM_API_KEY to your .env file.'
    };
  }

  try {
    const params = new URLSearchParams();
    params.set('offset', '0');
    params.set('limit', '1');
    params.set('ptype', 'o');

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    params.set('postedFrom', formatDateForSAM(thirtyDaysAgo));
    params.set('postedTo', formatDateForSAM(new Date()));

    const response = await fetch(`${SAM_PROXY_BASE}/opportunities/v2/search?${params.toString()}`, {
      method: 'GET',
      headers: {
        'X-Api-Key': apiKey,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        message: `SAM.gov API error: ${response.status} - ${text.substring(0, 100)}`
      };
    }

    const data = await response.json();
    return {
      success: true,
      message: `Connected! Found ${data.totalRecords || 0} opportunities available.`
    };
  } catch (error) {
    return {
      success: false,
      message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}
